// eslint-disable import/no-unresolved
import { restClient } from "@/lib/raftApi";
import { useDrupalSettings } from "@/composables/useDrupalSettings";

const { config } = useDrupalSettings();

const state = {
  hazAllLabs: [],
  wasteProfiles: [],
  selectedLabs: [],
  selectedWasteProfile: [],
  selectedDepartments: [],
  selectedBuildings: [],
  selectedCampuses: [],
  selectedHazards: [],
  hazWasteHazards: [],
  hazWasteMaterials: [],
  additionalClassifications: [],
  requestedFromDate: "",
  requestedToDate: "",
  hazFiltersUpdated: "",
  hazChemConstituentFilter: "",
  hazWasteFieldConfig: {},
  suppliesNeeded: [],
  pCodes: [],
  dCodes: [],
  fCodes: [],
  uCodes: [],
  stateCodes: [],
  selectedPCodes: [],
  selectedDCodes: [],
  selectedFCodes: [],
  selectedUCodes: [],
  selectedStateCodes: [],
  hazWasteGeneralConfig: {},
  pickupStatusMap: {
    rejected: "Rejected",
    pending_pickup: "Pending Pickup",
    revised: "Revised",
    closed: "Closed",
  },
};

const mutations = {
  setSelectedLabs: (state, labs) => (state.selectedLabs = labs),
  setHazAllLabs: (state, labs) => (state.hazAllLabs = labs),
  setSelectedDepartments: (state, dept) => (state.selectedDepartments = dept),
  setSelectedBuildings: (state, buildings) =>
    (state.selectedBuildings = buildings),
  setSelectedCampuses: (state, campuses) => (state.selectedCampuses = campuses),
  setSelectedHazards: (state, hazards) => (state.selectedHazards = hazards),
  setSelectedWasteProfile: (state, profile) =>
    (state.selectedWasteProfile = profile),
  setRequestedFromDate: (state, date) => (state.requestedFromDate = date),
  setRequestedToDate: (state, date) => (state.requestedToDate = date),
  setWasteProfiles: (state, profiles) => (state.wasteProfiles = profiles),
  setHazFiltersUpdated: (state, time) => (state.hazFiltersUpdated = time),
  setHazWasteFieldConfig: (state, fieldConfig) =>
    (state.hazWasteFieldConfig = fieldConfig),
  appendHazards: (state, hazards) =>
    (state.hazWasteHazards = state?.hazWasteHazards?.concat(hazards)),
  setHazWasteHazards: (state, hazards) => (state.hazWasteHazards = hazards),
  setHazWasteMaterials: (state, materials) =>
    (state.hazWasteMaterials = materials),
  setAdditionalClassifications: (state, classifications) =>
    (state.additionalClassifications = classifications),
  setSuppliesNeeded: (state, supplies) => (state.suppliesNeeded = supplies),
  setHazChemConstituentFilter: (state, chem) =>
    (state.hazChemConstituentFilter = chem),
  setPCodes: (state, codes) => (state.pCodes = codes),
  setFCodes: (state, codes) => (state.fCodes = codes),
  setUCodes: (state, codes) => (state.uCodes = codes),
  setDCodes: (state, codes) => (state.dCodes = codes),
  setStateCodes: (state, codes) => (state.stateCodes = codes),
  setSelectedFCodes: (state, codes) => (state.selectedFCodes = codes),
  setSelectedDCodes: (state, codes) => (state.selectedDCodes = codes),
  setSelectedUCodes: (state, codes) => (state.selectedUCodes = codes),
  setSelectedPCodes: (state, codes) => (state.selectedPCodes = codes),
  setSelectedStateCodes: (state, codes) => (state.selectedStateCodes = codes),
  setHazWasteGeneralConfig: (state, generalConfig) =>
    (state.hazWasteGeneralConfig = generalConfig),
};
const getters = {
  getPickupStatusMap: (state) => state.pickupStatusMap,
  getSelectedLabs: (state) => state.selectedLabs,
  getSelectedDepartments: (state) => state.selectedDepartments,
  getSelectedBuildings: (state) => state.selectedBuildings,
  getSelectedCampuses: (state) => state.selectedCampuses,
  getSelectedHazards: (state) => state.selectedHazards,
  getRequestedFromDate: (state) => state.requestedFromDate,
  getSelectedWasteProfile: (state) => state.selectedWasteProfile,
  getRequestedToDate: (state) => state.requestedToDate,
  getHazFiltersUpdated: (state) => state.hazFiltersUpdated,
  getHazWasteHazards: (state) => state.hazWasteHazards,
  getHazWasteMaterials: (state) => state.hazWasteMaterials,
  getAdditionalClassifications: (state) => state.additionalClassifications,
  getHazChemConstituentFilter: (state) => state.hazChemConstituentFilter,
  getFCodes: (state) => state.fCodes,
  getUCodes: (state) => state.uCodes,
  getPCodes: (state) => state.pCodes,
  getDCodes: (state) => state.dCodes,
  getStateCodes: (state) => state.stateCodes,
  getSelectedDCodes: (state) => state.selectedDCodes,
  getSelectedFCodes: (state) => state.selectedFCodes,
  getSelectedUCodes: (state) => state.selectedUCodes,
  getSelectedPCodes: (state) => state.selectedPCodes,
  getSelectedStateCodes: (state) => state.selectedStateCodes,
  getSuppliesNeeded: (state) => state.suppliesNeeded,
  getHazWasteFieldConfig: (state) => state.hazWasteFieldConfig,
  getHazWasteGeneralConfig: (state) => state.hazWasteGeneralConfig,
};

const actions = {
  clearHazSelectedFilters({ commit }, value) {
    commit("setSelectedLabs", []);
    commit("setSelectedDepartments", []);
    commit("setSelectedBuildings", []);
    commit("setSelectedCampuses", []);
    commit("setSelectedWasteProfile", []);
    commit("setSelectedHazards", []);
    commit("setRequestedFromDate", "");
    commit("setRequestedToDate", "");
    commit("setSelectedFCodes", []);
    commit("setSelectedDCodes", []);
    commit("setSelectedUCodes", []);
    commit("setSelectedPCodes", []);
    commit("setSelectedStateCodes", []);
  },
  async fetchHazWasteGeneralConfig({ state, commit, rootState }) {
    const options = {
      path: "/hazwaste-config",
      queries: {},
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setHazWasteGeneralConfig", response);
  },
  async fetchHazLabList({ state, commit, rootState }) {
    const response = await restClient
      .get({ path: "/laboratories", queries: {} })
      .catch((error) => console.error(error));
    commit("setHazAllLabs", response?.laboratories);
  },
  async fetchHazWasteFieldConfig({ state, commit, rootState }) {
    const options = {
      path: "/hazardous-waste/fields",
      queries: {},
    };
    if (config?.groupContext) {
      options.queries.group = config.groupContext;
    }
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });

    if (
      response?.waste_pickup_request.hasOwnProperty(
        "field_wpr_add_supplies_needed",
      )
    ) {
      const suppliesAllowed =
        response.waste_pickup_request.field_wpr_add_supplies_needed.settings
          .second.allowed_values;
      const suppliesNeeded = Object.keys(suppliesAllowed).map((key) => ({
        key: key,
        label: suppliesAllowed[key],
      }));
      commit("setSuppliesNeeded", suppliesNeeded);
    }
    commit("setHazWasteFieldConfig", response);
  },
  async fetchHazPublicProfileList({ state, commit, rootState }) {
    const options = {
      path: "/waste-stream/profiles/public",
      queries: {},
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setWasteProfiles", response?.results);
  },
  async fetchHazWasteHazardList({ state, commit, rootState }) {
    const options = {
      path: "/config_terms",
      queries: {
        vocab: "chemical_waste_stream_profile_hazards",
      },
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setHazWasteHazards", response?.config_terms);
  },
  async fetchHazWasteMaterials({ state, commit, rootState }) {
    const options = {
      path: "/config_terms",
      queries: {
        vocab: "waste_stream_profile_materials",
      },
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setHazWasteMaterials", response?.config_terms);
  },
  async fetchAdditionalClassifications({ state, commit, rootState }) {
    const options = {
      path: "/config_terms",
      queries: {
        vocab: "chemical_waste_stream_additional_classifications",
      },
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setAdditionalClassifications", response.config_terms);
  },
  async fetchSuppliesNeeded({ state, commit, rootState }) {
    const options = {
      path: "/config_terms",
      queries: {
        vocab: "wpr_add_supplies_needed",
      },
    };
    const response = await restClient.get(options).catch((error) => {
      /* eslint-disable no-console */
      console.error(error);
    });
    commit("setSuppliesNeeded", response.config_terms);
  },
  async fetchHazWasteCodes({ state, commit, rootState }) {
    const codeTypes = ["d_code", "f_code", "p_code", "u_code", "state_code"];

    for (const codeType of codeTypes) {
      // eslint-disable-next-line no-await-in-loop
      const response = await restClient
        .get({ path: "/hazardous-waste/waste-codes/" + codeType, queries: {} })
        .catch((error) => {
          /* eslint-disable no-console */
          console.error(error);
        });
      switch (codeType) {
        case "p_code":
          commit("setPCodes", response);
          break;

        case "u_code":
          commit("setUCodes", response);
          break;

        case "d_code":
          commit("setDCodes", response);
          break;

        case "f_code":
          commit("setFCodes", response);
          break;

        case "state_code":
          commit("setStateCodes", response);
          break;

        default:
          break;
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
